const open_accessible_modal_stack = []

export const accessible_modal_mixin = {
	data() {
		return {
			accessible_modal_focus_origin: null
		}
	},
	mounted() {
		if (typeof document === 'undefined') return
		document.addEventListener('keydown', this.handle_accessible_modal_document_keydown, true)
	},
	beforeDestroy() {
		if (typeof document !== 'undefined') {
			document.removeEventListener('keydown', this.handle_accessible_modal_document_keydown, true)
		}
		this.unregister_accessible_modal()
	},
	methods: {
		accessible_modal_is_open() {
			return false
		},
		get_accessible_modal_focus_origin() {
			if (typeof document === 'undefined') return null
			const has_html_element = typeof HTMLElement !== 'undefined'
			return has_html_element && document.activeElement instanceof HTMLElement ? document.activeElement : null
		},
		get_accessible_modal_container() {
			return null
		},
		get_accessible_modal_initial_focus_selector() {
			return this.initialFocusSelector || this.initial_focus_selector || null
		},
		get_accessible_modal_preferred_focus_target() {
			return null
		},
		should_auto_focus_accessible_modal() {
			return this.autoFocus !== false && this.auto_focus !== false
		},
		should_restore_accessible_modal_focus() {
			return this.restoreFocus !== false && this.restore_focus !== false
		},
		should_trap_accessible_modal_focus() {
			return this.trapFocus !== false && this.trap_focus !== false
		},
		should_close_accessible_modal_on_escape() {
			return true
		},
		is_visible_accessible_modal_focus_target(element) {
			if (!element) return false
			if (element.disabled) return false
			if (element.getAttribute('aria-hidden') === 'true') return false
			if (element.closest('[aria-hidden="true"]')) return false
			if (element.matches?.('input[type="hidden"]')) return false
			if (element.tabIndex < 0) return false
			const styles = window.getComputedStyle(element)
			if (styles.visibility === 'hidden' || styles.display === 'none' || styles.opacity === '0') return false
			if (element.getClientRects().length > 0) return true
			return typeof navigator !== 'undefined' && /jsdom/i.test(navigator.userAgent || '')
		},
		resolve_accessible_modal_focus_target(candidate) {
			if (!candidate) return null
			if (this.is_visible_accessible_modal_focus_target(candidate)) return candidate
			const descendant_candidates = Array.from(
				candidate.querySelectorAll?.('button, [href], input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])') || []
			)
			return descendant_candidates.find(element => this.is_visible_accessible_modal_focus_target(element)) || null
		},
		get_accessible_modal_focus_targets(container) {
			if (!container) return []
			const focus_targets = Array.from(
				container.querySelectorAll('button, [href], input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])')
			)
			return focus_targets.filter(element => this.is_visible_accessible_modal_focus_target(element))
		},
		visible_accessible_menu_in_modal(container) {
			if (!container || typeof document === 'undefined') return null
			const menu_panels = Array.from(document.querySelectorAll('.k-menu-accessible-popover'))
			return menu_panels.find(element => {
				if (!container.contains(element)) return false
				const styles = window.getComputedStyle(element)
				if (styles.visibility === 'hidden' || styles.display === 'none') return false
				return element.getClientRects().length > 0
			}) || null
		},
		active_accessible_menu_panel() {
			if (typeof document === 'undefined') return null
			const active_element = document.activeElement
			if (!active_element?.closest) return null
			const active_panel = active_element.closest('.k-menu-accessible-popover')
			if (!active_panel) return null
			const styles = window.getComputedStyle(active_panel)
			if (styles.visibility === 'hidden' || styles.display === 'none') return null
			if (!active_panel.getClientRects().length) return null
			return active_panel
		},
		capture_accessible_modal_focus_origin() {
			if (!this.should_restore_accessible_modal_focus()) return
			this.accessible_modal_focus_origin = this.get_accessible_modal_focus_origin()
		},
		restore_accessible_modal_focus_origin() {
			if (!this.should_restore_accessible_modal_focus()) return
			this.accessible_modal_focus_origin?.focus?.()
			this.accessible_modal_focus_origin = null
		},
		register_accessible_modal() {
			this.unregister_accessible_modal()
			open_accessible_modal_stack.push(this._uid)
		},
		unregister_accessible_modal() {
			const stack_index = open_accessible_modal_stack.lastIndexOf(this._uid)
			if (stack_index >= 0) open_accessible_modal_stack.splice(stack_index, 1)
		},
		is_topmost_accessible_modal() {
			return open_accessible_modal_stack[open_accessible_modal_stack.length - 1] === this._uid
		},
		focus_accessible_modal_content(attempts_remaining = 6) {
			if (!this.should_auto_focus_accessible_modal()) return
			this.$nextTick(() => {
				window.requestAnimationFrame(() => {
					const container = this.get_accessible_modal_container()
					if (!container) return

					if (document.activeElement !== container && !container.contains(document.activeElement)) {
						container.focus?.()
					}

					const preferred_target = this.get_accessible_modal_preferred_focus_target()
					const resolved_preferred_target = this.resolve_accessible_modal_focus_target(preferred_target)
					if (resolved_preferred_target) {
						resolved_preferred_target.focus()
						return
					}

					const initial_focus_selector = this.get_accessible_modal_initial_focus_selector()
					if (initial_focus_selector) {
						const selected_targets = Array.from(container.querySelectorAll(initial_focus_selector))
						for (const selected_target of selected_targets) {
							const resolved_target = this.resolve_accessible_modal_focus_target(selected_target)
							if (resolved_target) {
								resolved_target.focus()
								return
							}
						}
					}

					const annotated_target = container.querySelector(
						'[data-k-dialog-initial-focus="true"], [data-k-modal-initial-focus="true"]'
					)
					if (this.is_visible_accessible_modal_focus_target(annotated_target)) {
						annotated_target.focus()
						return
					}

					const first_focus_target = this.get_accessible_modal_focus_targets(container)[0]
					if (first_focus_target) {
						first_focus_target.focus()
						return
					}

					if (attempts_remaining > 0) {
						window.setTimeout(() => this.focus_accessible_modal_content(attempts_remaining - 1), 16)
					}
				})
			})
		},
		close_accessible_modal_from_escape(event) {
			this.$emit('keydown.esc', event)
			this.$emit('escape', event)
		},
		handle_accessible_modal_document_keydown(event) {
			if (!this.accessible_modal_is_open()) return
			if (!this.is_topmost_accessible_modal()) return
			if (this.active_accessible_menu_panel()) return

			if (event.key === 'Escape' || event.key === 'Esc') {
				if (!this.should_close_accessible_modal_on_escape()) return
				event.stopPropagation()
				this.close_accessible_modal_from_escape(event)
				return
			}

			if (event.key !== 'Tab' || !this.should_trap_accessible_modal_focus()) return

			const container = this.get_accessible_modal_container()
			if (!container) return
			if (this.visible_accessible_menu_in_modal(container)) return

			const focus_targets = this.get_accessible_modal_focus_targets(container)
			if (!focus_targets.length) {
				event.preventDefault()
				container.focus?.()
				return
			}

			const first_focus_target = focus_targets[0]
			const last_focus_target = focus_targets[focus_targets.length - 1]
			const active_element = document.activeElement
			const focus_is_inside = container.contains(active_element)

			if (event.shiftKey) {
				if (!focus_is_inside || active_element === first_focus_target) {
					event.preventDefault()
					last_focus_target.focus()
				}
				return
			}

			if (!focus_is_inside || active_element === last_focus_target) {
				event.preventDefault()
				first_focus_target.focus()
			}
		}
	}
}
